import { Fragment, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import PlatformBadge from './PlatformBadge';
import SortHeader from './SortHeader';
import {
  groupMeta,
  groupLabel,
  groupTypeLabel,
  columnsForGroup,
  headlineMetric,
  contextualTone,
  TONE_CLASSES,
  formatCell,
  budgetInfo,
  sortRows,
} from '../campaignObjectives';

function StatusDot({ status }) {
  const active = status ? /active|enabled/i.test(status) : false;
  return (
    <span
      className={`h-1.5 w-1.5 shrink-0 rounded-full ${active ? 'bg-emerald-500' : 'bg-gray-300'}`}
      title={status ?? 'sin estado'}
    />
  );
}

function Cell({ col, row, heatValues }) {
  if (col.format === 'budget') {
    const b = budgetInfo(row);
    return (
      <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">
        {b ? (
          <>
            {b.text}
            {b.hint && <span className="ml-1 text-xs text-gray-400">{b.hint}</span>}
          </>
        ) : (
          '—'
        )}
      </td>
    );
  }
  const text = formatCell(row[col.key], col.format);
  if (col.heatmap) {
    const tone = contextualTone(row[col.key], heatValues, col.heatmap);
    return (
      <td className="px-2 py-2.5 text-right">
        <span
          className={`inline-block min-w-[3rem] rounded-md px-2 py-1 tabular-nums ${
            tone ? TONE_CLASSES[tone] : 'text-gray-900'
          }`}
        >
          {text}
        </span>
      </td>
    );
  }
  return <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{text}</td>;
}

function TreeRow({ level, expandable, expanded, onToggle, name, row, columns, heat }) {
  return (
    <tr className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 ${level > 0 ? 'bg-gray-50/40' : ''}`}>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 20}px` }}>
          {expandable ? (
            <button
              type="button"
              onClick={onToggle}
              className="text-gray-400 hover:text-gray-700"
              aria-label={expanded ? 'Contraer' : 'Expandir'}
            >
              <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </button>
          ) : (
            <span className="inline-block w-4 shrink-0" />
          )}
          {name}
        </div>
      </td>
      {columns.map((col) => (
        <Cell key={col.key} col={col} row={row} heatValues={heat[col.key]} />
      ))}
    </tr>
  );
}

function heatFor(rows, columns) {
  const map = {};
  columns.forEach((c) => {
    if (c.heatmap) map[c.key] = rows.map((r) => r[c.key]);
  });
  return map;
}

/**
 * Un grupo de objetivo: header colapsable + tabla-árbol con drill
 * campaña → conjunto → anuncio. Orden por header, escala de color contextual
 * relativa a los hermanos de cada nivel. Los anuncios salen del `ads[]` plano
 * filtrado por group_id.
 */
export default function CampaignGroup({ group, ads = [], defaultOpen = true, onPreview }) {
  const code = group.objective_group;
  const [open, setOpen] = useState(defaultOpen);
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });
  const [openCampaigns, setOpenCampaigns] = useState(() => new Set());
  const [openGroups, setOpenGroups] = useState(() => new Set());

  const columns = columnsForGroup(code);
  const meta = groupMeta(code);
  const headline = headlineMetric(code);
  const totals = group.totals ?? {};

  const adsByGroup = useMemo(() => {
    const map = {};
    ads.forEach((a) => {
      if (a.group_id) (map[a.group_id] ??= []).push(a);
    });
    return map;
  }, [ads]);

  const campaigns = useMemo(() => sortRows(group.campaigns, sort.key, sort.dir), [group.campaigns, sort]);
  const campaignHeat = useMemo(() => heatFor(group.campaigns, columns), [group.campaigns, columns]);

  // El header ordena por la columna elegida; el nombre cae a spend en los niveles hijos.
  const nestedKey = sort.key === 'campaign_name' ? 'spend' : sort.key;

  const onSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const toggle = (setter) => (id) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleCampaign = toggle(setOpenCampaigns);
  const toggleGroup = toggle(setOpenGroups);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 bg-gray-50 px-4 py-2.5 text-left hover:bg-gray-100"
      >
        <span className="flex items-center gap-2.5">
          <span aria-hidden="true" className="text-gray-400">{open ? '▾' : '▸'}</span>
          <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${meta.pill}`}>
            {groupLabel(code)}
          </span>
          <span className="text-xs text-gray-500">
            {totals.campaign_count} {totals.campaign_count === 1 ? 'campaña' : 'campañas'}
          </span>
        </span>
        <span className="text-xs text-gray-600">
          Gasto <span className="font-medium text-gray-900">{formatCell(totals.spend, 'currency')}</span>
          {headline && totals[headline.key] != null && (
            <>
              {' · '}
              {headline.label}{' '}
              <span className="font-medium text-gray-900">
                {formatCell(totals[headline.key], headline.format)}
              </span>
            </>
          )}
        </span>
      </button>

      {open && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                <SortHeader sortKey="campaign_name" sort={sort} onSort={onSort} align="left">
                  Campaña
                </SortHeader>
                {columns.map((c) => (
                  <SortHeader key={c.key} sortKey={c.key} sort={sort} onSort={onSort}>
                    {c.label}
                  </SortHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const cKey = `${c.platform}-${c.campaign_id}`;
                const adGroups = sortRows(c.ad_groups ?? [], nestedKey, sort.dir);
                const cExpanded = openCampaigns.has(cKey);
                const groupHeat = heatFor(c.ad_groups ?? [], columns);
                return (
                  <Fragment key={cKey}>
                    <TreeRow
                      level={0}
                      expandable={adGroups.length > 0}
                      expanded={cExpanded}
                      onToggle={() => toggleCampaign(cKey)}
                      row={c}
                      columns={columns}
                      heat={campaignHeat}
                      name={
                        <>
                          <PlatformBadge platform={c.platform} status={c.status} />
                          <span className="text-gray-900">{c.campaign_name || c.campaign_id}</span>
                          {c.is_overridden && (
                            <span
                              className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500"
                              title="Objetivo reasignado manualmente"
                            >
                              override
                            </span>
                          )}
                        </>
                      }
                    />
                    {cExpanded &&
                      adGroups.map((ag) => {
                        const agKey = `${ag.platform}-${ag.group_id}`;
                        const agAds = sortRows(adsByGroup[ag.group_id] ?? [], nestedKey, sort.dir);
                        const agExpanded = openGroups.has(agKey);
                        const adHeat = heatFor(adsByGroup[ag.group_id] ?? [], columns);
                        return (
                          <Fragment key={agKey}>
                            <TreeRow
                              level={1}
                              expandable={agAds.length > 0}
                              expanded={agExpanded}
                              onToggle={() => toggleGroup(agKey)}
                              row={ag}
                              columns={columns}
                              heat={groupHeat}
                              name={
                                <>
                                  <StatusDot status={ag.status} />
                                  <span className="text-gray-700">{ag.group_name || ag.group_id}</span>
                                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs uppercase tracking-wide text-gray-400">
                                    {groupTypeLabel(ag.group_type)}
                                  </span>
                                </>
                              }
                            />
                            {agExpanded &&
                              agAds.map((ad) => (
                                <TreeRow
                                  key={`${ad.platform}-${ad.ad_id}`}
                                  level={2}
                                  expandable={false}
                                  row={ad}
                                  columns={columns}
                                  heat={adHeat}
                                  name={
                                    <>
                                      <StatusDot status={ad.status} />
                                      <span className="text-gray-600">{ad.ad_name || ad.ad_id}</span>
                                      {ad.creative_id && onPreview && (
                                        <button
                                          type="button"
                                          onClick={() => onPreview(ad)}
                                          className="text-xs font-medium text-indigo-600 hover:underline"
                                        >
                                          Ver
                                        </button>
                                      )}
                                    </>
                                  }
                                />
                              ))}
                          </Fragment>
                        );
                      })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
